/**
 * Commercial package feature matrix for DEV package-tier preview.
 * Mirrors SSOT seed migrations (20260508 + billing/public-site + HR margins).
 * FE-only — does not mutate the real session or subscription.
 */

/** @typedef {"BASIC" | "PRO" | "AGENCY"} PackageTier */

/** @type {Record<PackageTier, Record<string, boolean|number|null>>} */
const MATRIX = {
  BASIC: {
    commission_percentage: 95,
    crazy_llama: false,
    dynamic_packages: false,
    hotel_hr_commission_percentage: 5,
    vacation_package_hr_commission_percentage: 5,
    flight_hr_commission_percentage: 5,
    organized_tour_hr_commission_percentage: 5,
    hotel_prebooking: false,
    leads_system: true,
    max_monthly_orders: 50,
    organized_tours: false,
    priority_support: false,
    referral_rewards: false,
    search_flights: false,
    search_hotels: true,
    search_packages: true,
    seo_system: false,
    smart_quotes: false,
    team_management: false,
    billing_access: false,
    agent_public_site: false,
    mano_quotes: false,
    monthly_pricing: false,
    customer_offer_link: false,
    multi_package_customer_offer: false,
  },
  PRO: {
    commission_percentage: 100,
    crazy_llama: true,
    dynamic_packages: true,
    hotel_hr_commission_percentage: 10,
    vacation_package_hr_commission_percentage: 10,
    flight_hr_commission_percentage: 10,
    organized_tour_hr_commission_percentage: 10,
    hotel_prebooking: true,
    leads_system: true,
    max_monthly_orders: null,
    organized_tours: true,
    priority_support: true,
    referral_rewards: true,
    search_flights: true,
    search_hotels: true,
    search_packages: true,
    seo_system: true,
    smart_quotes: true,
    team_management: true,
    billing_access: true,
    agent_public_site: true,
    mano_quotes: false,
    monthly_pricing: true,
    customer_offer_link: true,
    multi_package_customer_offer: true,
  },
  AGENCY: {
    commission_percentage: 100,
    crazy_llama: true,
    dynamic_packages: true,
    hotel_hr_commission_percentage: 10,
    vacation_package_hr_commission_percentage: 10,
    flight_hr_commission_percentage: 10,
    organized_tour_hr_commission_percentage: 10,
    hotel_prebooking: true,
    leads_system: true,
    max_monthly_orders: null,
    organized_tours: true,
    priority_support: true,
    referral_rewards: false,
    search_flights: true,
    search_hotels: true,
    search_packages: true,
    seo_system: true,
    smart_quotes: true,
    team_management: true,
    billing_access: true,
    agent_public_site: true,
    mano_quotes: false,
    monthly_pricing: true,
    customer_offer_link: true,
    multi_package_customer_offer: true,
  },
};

/**
 * @param {PackageTier} tier
 * @returns {Record<string, boolean|number|null>}
 */
export function getDevPackageFeatureMatrix(tier) {
  const row = MATRIX[tier];
  return row ? { ...row } : {};
}

/**
 * Apply a DEV package-tier override onto an entitlements API payload.
 * Strips admin PRO overlay so gates follow the selected tier.
 *
 * @param {object|null|undefined} payload
 * @param {PackageTier|null|undefined} tier
 * @returns {object|null|undefined}
 */
export function applyDevPackageTierToEntitlementsPayload(payload, tier) {
  if (!tier || !MATRIX[tier]) return payload;

  const features = getDevPackageFeatureMatrix(tier);
  const base =
    payload && typeof payload === "object" ? { ...payload } : {};

  const prevAudit =
    base.entitlementsAudit && typeof base.entitlementsAudit === "object"
      ? { ...base.entitlementsAudit }
      : {};

  return {
    ...base,
    effectivePackageCode: tier,
    package: {
      ...(base.package && typeof base.package === "object" ? base.package : {}),
      code: tier,
    },
    features,
    effectiveCommissionPercentage: features.commission_percentage,
    entitlementsAudit: {
      ...prevAudit,
      adminProEntitlementsOverlay: false,
      devPackageTierOverride: tier,
    },
  };
}
